/**
 * Write-only credential fields — V9.1.
 *
 * ## The failure this ends
 *
 * A secret field never shows what it holds, so the save payload has to encode
 * *intent* rather than a value: keep what is stored, replace it, or clear it.
 * Until V9.1 that intent was inferred from the value's **shape** — `undefined`
 * meant keep, `''` meant clear, anything else meant replace — and the shape a
 * form produces is not something a form is careful about.
 *
 * The cost, three times over on live AWS keys: a save that sent an empty secret
 * beside a freshly pasted access key id **deleted** the stored secret and wrote
 * the id. Resolution then filled the missing half from `S3_SECRET_ACCESS_KEY`,
 * so the store held the new id and the environment supplied an old secret — a
 * mixed pair, and `SignatureDoesNotMatch` naming the key id that had just been
 * saved. Everything on screen said the save had worked, because it had: it had
 * saved exactly what was sent.
 *
 * So intent is now explicit and unmissable:
 *
 *   - `KEEP_EXISTING_SECRET` — the field was never touched. The **only** way to
 *     keep a stored secret through a request that mentions the field at all.
 *   - `null` — clear it, deliberately.
 *   - any other string — the operator typed or pasted this. It always wins,
 *     trimmed of surrounding whitespace.
 *   - `''` — no longer meaningful, and rejected rather than guessed at. It is
 *     what a form emits by accident, and guessing cost three rounds of live
 *     keys.
 *
 * Shared by `packages/types` so the browser and the API cannot drift on the
 * spelling of the sentinel — which would silently turn "keep" into "replace
 * with the literal string `__vt_unchanged__`".
 */
/**
 * "This field was not touched — leave what is stored."
 *
 * Deliberately not a plausible credential: no AWS key, Brevo key, VAPID half or
 * Meta token has this shape, so a real secret can never be mistaken for it.
 */
export const KEEP_EXISTING_SECRET = '__vt_unchanged__';
/** True only for the untouched sentinel — never for a value someone pasted. */
export function isKeepExistingSecret(value) {
    return value === KEEP_EXISTING_SECRET;
}
//# sourceMappingURL=secrets.js.map